import {useEffect, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {useMyCoupons} from '@/api/coupon';
import {ChevronRightIconComponent} from '@/components/Icons';
import {
  COUPON_ENTRY_CARD_VIEW_SESSION_KEY,
  COUPON_EVENTS,
  COUPON_MODAL_OPEN_PARAM,
} from '@/constants';
import {useSession} from '@/hooks';
import {useGA4WithParams} from '@/hooks/tracking';
import {paths} from '@/router';
import {Flexbox, FlexFullGrow} from '@/styled-components';
import {MEDIA_QUERIES, palette} from '@/styles';
import {MyCouponModal} from './MyCouponModal';

/**
 * /plans 이용권 카드 아래에 상시 노출하는 보유 쿠폰 진입 카드.
 * 보유 쿠폰이 0개인 회원·비회원에게도 노출한다 (사후 공유 코드 등록 경로 유지).
 */
export const CouponEntryCard = () => {
  const navigate = useNavigate();
  const {pathname, search} = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tracking = useGA4WithParams();
  const {hasSession, user, isPaidUser} = useSession();
  const {data: coupons, isPending} = useMyCoupons(user?.id);
  const [showModal, setShowModal] = useState<boolean>(false);

  const count = coupons?.length ?? 0;
  const activePass = isPaidUser ? 'Y' : 'N';
  const isCountReady = !hasSession || !isPending;

  const openModal = () => {
    setShowModal(true);
    tracking(COUPON_EVENTS.modalOpen, {
      available_count: count,
      active_pass: activePass,
    });
  };

  // 카드 렌더 완료 시점에 세션당 1회 전송
  useEffect(() => {
    if (!isCountReady || sessionStorage.getItem(COUPON_ENTRY_CARD_VIEW_SESSION_KEY)) {
      return;
    }

    sessionStorage.setItem(COUPON_ENTRY_CARD_VIEW_SESSION_KEY, 'true');
    tracking(COUPON_EVENTS.entryCardView, {
      available_count: count,
      active_pass: activePass,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCountReady]);

  // 로그인 알림에서 이동한 경우 모달을 바로 연다.
  useEffect(() => {
    if (!hasSession || !searchParams.get(COUPON_MODAL_OPEN_PARAM)) {
      return;
    }

    searchParams.delete(COUPON_MODAL_OPEN_PARAM);
    setSearchParams(searchParams, {replace: true});
    openModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, searchParams]);

  const handleClick = () => {
    if (!hasSession) {
      navigate(paths.signin, {
        state: {
          from: {pathname, search},
        },
      });
      return;
    }

    openModal();
  };

  return (
    <>
      <Root>
        <CardTitle>
          {count > 0 ? `보유 쿠폰 ${count}장` : '쿠폰을 등록해 주세요'}
        </CardTitle>
        <EntryBox onClick={handleClick}>
          <Flexbox gap="8px">
            {count > 0
              ? '쿠폰을 확인하고 사용해 보세요'
              : '쿠폰 코드를 등록하고 사용해 보세요'}
            <FlexFullGrow />
            <ChevronRightIconComponent color={palette.gray500} />
          </Flexbox>
        </EntryBox>
      </Root>
      {showModal && <MyCouponModal onClose={() => setShowModal(false)} />}
    </>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 20px;
  background-color: ${palette.white};
  box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.1);
  padding: 24px 28px;
  margin-top: 20px;
  ${MEDIA_QUERIES.mobile} {
    padding: 20px 16px;
  }
`;

const CardTitle = styled.div`
  color: ${palette.gray900};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
`;

const EntryBox = styled.div`
  border: solid 1px ${palette.gray200};
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  color: ${palette.gray600};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  &:hover {
    background-color: ${palette.gray100};
  }
`;
